import { TableContainer, TableContents, TableTop } from "style/admin/table";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { formatDateTime, formatNumber } from "utils/util";
import { useNavigate } from "react-router-dom";
import { AdminReviewDto } from "interface/admin/interface.review";
import PaginationControls from "components/admin/table.components/pagination";

interface DataProps {
  data: AdminReviewDto[];
}

const AdminUserDetailReviewTable = ({ data }: DataProps) => {
  const navigate = useNavigate();
  // ✅ 최신 등록일(`regDt`)을 기준으로 내림차순 정렬
  const sortedData = useMemo(() => {
    return [...data].sort(
      (a, b) => new Date(b.regDt).getTime() - new Date(a.regDt).getTime()
    );
  }, [data]);

  const columns = useMemo<ColumnDef<AdminReviewDto>[]>(
    () => [
      {
        header: "Index",
        id: "number", // 넘버링을 위한 id
        cell: ({ row }) => sortedData.length - row.index, // 각 행의 인덱스에 1을 더해 넘버링
        size: 128,
      },
      {
        header: "제목",
        accessorKey: "title",
        cell: (info) => {
          const value = info.getValue();
          return (
            <div
              className="title-box"
              onClick={() =>
                clickedRowHandler(
                  info.row.original.reviewId,
                  info.row.original.regUserId
                )
              }
            >
              {String(value)}
            </div>
          );
        },
        size: 329,
      },
      {
        header: "작성자",
        accessorKey: "userName",
        cell: (info) => {
          const row = info.row.original; // ✅ 현재 행(row)의 원본 데이터 가져오기
          const userName = row.userName;
          const userProfileUrl =
            row.userProfileUrl || "/assets/icon/userprofile.png"; // 기본 이미지 설정

          return userName ? (
            <div className="profile">
              <div className="img-box">
                <img
                  src={
                    userProfileUrl
                      ? userProfileUrl
                      : "/assets/icon/userprofile.png"
                  }
                  alt={`${userName} 프로필`}
                />
              </div>
              <span>{userName}</span> {/* ✅ 텍스트 올바르게 출력 */}
            </div>
          ) : (
            "-"
          );
        },
        size: 160,
      },
      {
        header: "작성일시",
        accessorKey: "regDt",
        cell: (info) => {
          const value = info.getValue(); // ✅ 값 가져오기
          return value ? formatDateTime(String(value)) : "-"; // ✅ 값이 없으면 "-" 반환
        },
        size: 160,
      },
      {
        header: "조회",
        accessorKey: "views",
        cell: (info) => formatNumber(Number(info.getValue())),
        size: 100,
      },
      {
        header: "댓글",
        accessorKey: "reviewCommentCount",
        cell: (info) => (info.getValue() ? info.getValue() : "0"),
        size: 100,
      },
      {
        header: "신고",
        accessorKey: "reviewReportCount",
        cell: (info) => {
          const row = info.row.original;
          const targetId = row.reviewId;
          const value = info.getValue(); // ✅ 값 가져오기
          return value ? (
            <span
              className={`report-count ${
                Number(value) >= 10 ? "report-txt" : ""
              }`}
              onClick={() => {
                handleClickedReportlist(String(targetId), "REVIEW");
              }}
            >{`신고(${value})`}</span>
          ) : (
            "-"
          ); // ✅ 값이 없으면 "-" 반환
        },
        size: 100,
      },
      {
        header: "게시상태",
        accessorKey: "hideYn",
        cell: (info) => {
          const value = info.getValue(); // ✅ 값 가져오기
          return value === "N" ? (
            <div className="bluebox hide-item">정상노출</div>
          ) : (
            <div className="redbox hide-item">숨겨짐</div>
          );
        },
        size: 96,
      },
    ],
    [sortedData]
  );

  const table = useReactTable({
    data: sortedData,
    columns,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 50 } }, // ✅ 초기 페이지 크기 설정
  });

  const paginationState = table.getState().pagination;
  const startRow = paginationState.pageIndex * paginationState.pageSize + 1;
  const endRow = startRow + table.getRowModel().rows.length - 1;
  const totalRows = sortedData.length;
  const hostURL = process.env.REACT_APP_DEV_URL;

  const clickedRowHandler = (id: number, userId: string) => {
    window.open(
      `${hostURL}/admin/user/detail/review/${id}?userId=${userId}`,
      "_blank",
      "width=1240,height=800,scrollbars=no,resizable=no,toolbar=no,menubar=no,location=no,status=no"
    );
  };

  const handleClickedReportlist = (id: string, type: string) => {
    window.open(
      `${hostURL}/admin/report/list/${id}?type=${type}`,
      "_blank",
      "width=1240,height=800,scrollbars=no,resizable=no,toolbar=no,menubar=no,location=no,status=no"
    );
  };

  return (
    <TableContainer>
      <TableTop>
        <span className="table-count">
          {totalRows} 중 {startRow}-{endRow}
        </span>
        <PaginationControls table={table} />
      </TableTop>
      <TableContents>
        <table
          className={
            table.getRowModel().rows.length === 0 ? "no-data-table" : ""
          }
        >
          <thead>
            {table.getHeaderGroups().map((item) => (
              <tr key={item.id}>
                {item.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ minWidth: `${header.getSize()}px` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onDoubleClick={() =>
                    clickedRowHandler(
                      row.original.reviewId,
                      row.original.regUserId
                    )
                  } // ✅ 더블 클릭 시 상세 페이지 이동
                  style={{ cursor: "pointer" }} // ✅ 마우스 오버 시 클릭 가능하도록 스타일 추가
                >
                  {row.getVisibleCells().map((cell) => {
                    const isHideYn = cell.column.id === "hideYn";
                    const isTitle = cell.column.id === "title";
                    const isProfile = cell.column.id === "userName";

                    return (
                      <td
                        key={cell.id}
                        className={`${isHideYn ? "hide-state" : ""} ${
                          isTitle ? "title-cell" : ""
                        } ${isProfile ? "profile-cell" : ""}`.trim()} // ✅ 여러 개의 클래스 추가
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              // ✅ 데이터가 없을 경우 "검색 결과가 없습니다" 표시
              <tr className="no-data">
                <td colSpan={columns.length} className="no-data-td">
                  검색 결과가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </TableContents>
    </TableContainer>
  );
};

export default AdminUserDetailReviewTable;
